import { BaseApiClient } from './BaseApiClient';
import { IApiInterface } from './IApiConfig';
import { IAuthTokenBuilder } from './IAuthTokenBuilder';

export class NutritionClient extends BaseApiClient {
  private logger: any;

  public constructor(config: IApiInterface, fetch: any, logger: any, authTokenBuilder: IAuthTokenBuilder,
                     googleAnalytics?: any) {
    super(config, fetch, authTokenBuilder, googleAnalytics);
    this.logger = logger || (() => {
      // empty
    });
  }

  public saveFoodPreferences = async (foodPreference: string, useImperialUnit: boolean,
                                      dietGoal: string, gender: string) => {
    this.logger(`[NutritionClient] saving food preferences ${foodPreference}`);
    const request = await this.buildPostRequest(`api/nutrition/preferences/foodpreference`, {
      foodPreference,
      useImperialUnit,
      dietGoal,
      gender,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public saveUnitPreference = async (useImperialUnit: boolean) => {
    this.logger(`[NutritionClient] saving useImperialUnit as ${useImperialUnit}`);
    const request = await this.buildPostRequest(`api/nutrition/preferences/unitpreference`, {
      useImperialUnit,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public saveDietGoal = async (dietGoal: string, gender: string) => {
    this.logger(`[NutritionClient] saving diet goal ${dietGoal}`);
    const request = await this.buildPostRequest(`api/nutrition/preferences/dietgoal`, {
      dietGoal,
      gender,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getShoppingList = async () => {
    this.logger(`[NutritionClient] get shopping list`);
    const request = await this.buildGetRequest(`api/nutrition/shoppinglist`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getMealPlan = async () => {
    this.logger(`[NutritionClient] get meal plan`);
    const request = await this.buildGetRequest(`api/nutrition/meal-plan`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getMealImage = async (mealId: string) => {
    this.logger(`[NutritionClient] get image for meal ${mealId}`);
    const request = await this.buildGetRequest(`api/nutrition/meals/${mealId}/image`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getThumbnailImage = async (mealId: string) => {
    this.logger(`[NutritionClient] get thumbnail for ${mealId}`);
    const request = await this.buildGetRequest(`api/nutrition/meals/${mealId}/thumbnail`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getThumbnails = async (mealPlanId: string) => {
    this.logger(`[NutritionClient] get thumbnails for ${mealPlanId}`);
    const request = await this.buildGetRequest(`api/nutrition/mealplan/${mealPlanId}/thumbnails`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getMealsForTimeOfDay = async (timeOfDay: string, mealPlanId: string) => {
    this.logger(`[NutritionClient] get meals for the time of day ${timeOfDay}`);
    const request = await this.buildGetRequest(`api/nutrition/mealplan/${mealPlanId}/meals-time-of-day/${timeOfDay}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public setUserMealPlan = async (mealId: string, mealOfDay: string, weekDay: string) => {
    this.logger(`[NutritionClient] setting mealId ${mealId} for meal of day ${mealOfDay},
                  weekday ${weekDay}`);
    const request = await this.buildPostRequest(`api/nutrition/usermealplan`, {
      mealId,
      mealOfDay,
      weekDay,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public setShoppingListSettings = async (
    exclusions: /* models' ShoppingListExclusion[] */ any[],
    shoppingListServingCount = 1,
  ) => {
    this.logger('[NutritionClient] set shopping list settings');
    const request = await this.buildPostRequest('api/nutrition/shoppinglist/settings', {
      shoppingListExclusions: exclusions,
      shoppingListServingCount,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public setChecklistExclusions = async (exclusions: any) => {
    this.logger(`[NutritionClient] set checklist exclusions ${exclusions} `);
    const request = await this.buildPostRequest(`api/nutrition/shoppinglist/checklist`, exclusions);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public captureNutritionRating = async (date: Date, rating: 1 | 2 | 3 | 4 | 5) => {
    this.logger(`[NutritionClient] set rating ${rating} for ${date}`);
    const request = await this.buildPostRequest(`api/nutrition/rating`, { date, rating });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getLastNutritionRating = async () => {
    this.logger(`[NutritionClient] get the last rating`);
    const request = await this.buildGetRequest(`api/nutrition/lastrating`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }
}
